import * as React from "react";
import { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";

interface FolderDialogProps {
  open: boolean;
  mode: "create" | "rename";
  initialName?: string;
  onClose: () => void;
  onSubmit: (name: string, isPublic: boolean, isGlobal: boolean) => void;
  effectiveRole: string;
  globalTeamId: number;
  error?: string | null;
}

export default function FolderDialog({
  open,
  mode,
  initialName = "",
  onClose,
  onSubmit,
  effectiveRole,
  globalTeamId,
  error,
}: FolderDialogProps) {
  const [folderName, setFolderName] = useState(initialName);
  const [visibility, setVisibility] = useState<"private" | "public" | "global">(
    "private"
  );
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFolderName(initialName);
      setVisibility("private");
      setNameError(null);
    }
  }, [open, initialName]);

  const isOperatorOrAdmin =
    effectiveRole === "admin" || effectiveRole === "operator";
  const isFinnTeam = globalTeamId === 1;

  const handleSubmit = () => {
    const trimmed = folderName.trim();
    if (!trimmed) {
      setNameError("Folder name is required");
      return;
    }
    if (trimmed.length > 100) {
      setNameError("Folder name must be 100 characters or less");
      return;
    }
    setNameError(null);

    const isPublic = visibility === "public" || visibility === "global";
    const isGlobal = visibility === "global";
    onSubmit(trimmed, isPublic, isGlobal);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: mode === "create" ? "primary.50" : "info.50",
            }}
          >
            {mode === "create" ? (
              <CreateNewFolderIcon
                sx={{ color: "primary.main", fontSize: 20 }}
              />
            ) : (
              <DriveFileRenameOutlineIcon
                sx={{ color: "info.main", fontSize: 20 }}
              />
            )}
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {mode === "create" ? "Create Folder" : "Rename Folder"}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, px: 3, pb: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          label="Folder Name"
          fullWidth
          value={folderName}
          onChange={(e) => {
            setFolderName(e.target.value);
            if (nameError) setNameError(null);
          }}
          error={!!nameError}
          helperText={nameError || `${folderName.length}/100 characters`}
          inputProps={{ maxLength: 100 }}
          sx={{
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />
        {mode === "create" && (
          <FormControl sx={{ mt: 2.5 }} component="fieldset">
            <FormLabel
              component="legend"
              sx={{ fontWeight: 500, fontSize: "0.875rem" }}
            >
              Visibility
            </FormLabel>
            <RadioGroup
              value={visibility}
              onChange={(e) =>
                setVisibility(e.target.value as "private" | "public" | "global")
              }
            >
              <FormControlLabel
                value="private"
                control={<Radio size="small" />}
                label="Private (only you can see)"
              />
              {isOperatorOrAdmin && (
                <FormControlLabel
                  value="public"
                  control={<Radio size="small" />}
                  label="Public (visible to team)"
                />
              )}
              {isOperatorOrAdmin && isFinnTeam && (
                <FormControlLabel
                  value="global"
                  control={<Radio size="small" />}
                  label="Global (visible to all teams)"
                />
              )}
            </RadioGroup>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Button onClick={onClose} sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ borderRadius: 2, px: 3 }}
        >
          {mode === "create" ? "Create" : "Rename"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
